import { fmtDate } from '../../utils/datetime';
import { toast } from '../../utils/toast';
import React, { useState, useMemo, useEffect } from 'react';
import Select from "react-select";
import { ChevronLeft, ChevronRight, Edit, Trash2, Search, Filter, X } from 'lucide-react';
import { FirmListProps } from '../common/types';
import { PaginationMeta } from '../../api/doctor';
import { FirmServerFilters } from '../../api/firm';
import { useAuth } from '../../context/AuthContext';
import { capitalizeFirstLetter } from '../../utils/utils';
import EmployeeMain from '../../types/employee';
import { getAllHeadquarters, getAllLocations, getAllZones } from '../../api/master';
import { getAllEmployees } from '../../api/employee';

type SelectOption = { value: number; label: string; };
interface Zone { id: number; name: string; }
interface HeadQuarter { id: number; name: string; }
interface Location { id: number; name: string; head_quarter_id: number; latitude: string; longitude: string; }

interface FirmListExtendedProps extends FirmListProps {
  pagination?: PaginationMeta;
  onPageChange?: (page: number) => void;
  serverFilters?: FirmServerFilters;
  onServerFilterChange?: (filters: FirmServerFilters) => void;
}

const FirmList: React.FC<FirmListExtendedProps> = ({
  firms, isLoading, onEdit, onDelete,
  pagination, onPageChange,
  serverFilters = {}, onServerFilterChange,
}) => {
  const [searchTerm, setSearchTerm] = useState(serverFilters.search ?? '');
  const [clientFilters, setClientFilters] = useState({ type: "", zone_id: 0, location_id: 0, employee_id: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const [zones, setZones] = useState<Zone[]>([]);
  const [headQuarters, setHeadQuarters] = useState<HeadQuarter[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);
  const [employees, setEmployees] = useState<EmployeeMain[]>([]);
  const { user } = useAuth();

  useEffect(() => {
    if (!clientFilters.location_id) { setFilteredLocations([]); return; }
    setFilteredLocations(locations.filter(loc => loc.head_quarter_id === (serverFilters.head_quarter_id ?? 0)));
  }, [serverFilters.head_quarter_id, locations, clientFilters.location_id]);

  useEffect(() => {
    if (!user?.tenant_id) return;
    const fetchDropdowns = async () => {
      try {
        const [zoneData, hqData, locationData, employeeData] = await Promise.all([
          getAllZones(user.tenant_id),
          getAllHeadquarters(user.tenant_id),
          getAllLocations(user.tenant_id),
          getAllEmployees(user.tenant_id),
        ]);
        setZones(zoneData);
        setHeadQuarters(hqData);
        setLocations(locationData);
        setEmployees(employeeData);
      } catch (error) {
        console.error('Error fetching filter data:', error);
      }
    };
    fetchDropdowns();
  }, [user?.tenant_id]);

  // Debounce search input and push it to the server as a filter so it
  // searches the whole dataset, not just the currently loaded page.
  useEffect(() => {
    const handle = setTimeout(() => {
      if ((serverFilters.search ?? '') !== searchTerm) {
        onServerFilterChange?.({ ...serverFilters, search: searchTerm || undefined });
      }
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleHqChange = (value: string) => {
    const hqId = value ? Number(value) : undefined;
    // reset location (server-side) when HQ changes
    onServerFilterChange?.({ ...serverFilters, head_quarter_id: hqId, location_id: undefined });
    setClientFilters(prev => ({ ...prev, location_id: 0 }));
  };

  const handleLocationServerChange = (value: string) => {
    const locationId = value ? Number(value) : undefined;
    onServerFilterChange?.({ ...serverFilters, location_id: locationId });
  };

  // Locations for the location dropdown, filtered by selected HQ
  const hqFilteredLocations = serverFilters.head_quarter_id
    ? locations.filter(l => l.head_quarter_id === serverFilters.head_quarter_id)
    : locations;

  // Search is applied server-side (see debounce effect above) so that it
  // matches across the entire dataset rather than only the loaded page.
  const filteredFirms = useMemo(() => {
    return firms.filter(firm => {
      const typeMatch = !clientFilters.type || firm.type === clientFilters.type;
      const zoneMatch = !clientFilters.zone_id || firm.zone_id === clientFilters.zone_id;
      const locationMatch = !clientFilters.location_id || firm.location_id === clientFilters.location_id;
      const employeeMatch = !clientFilters.employee_id || firm.employee_id === clientFilters.employee_id;
      return typeMatch && zoneMatch && locationMatch && employeeMatch;
    });
  }, [firms, clientFilters]);

  const clearAllFilters = () => {
    setSearchTerm('');
    setClientFilters({ type: "", zone_id: 0, location_id: 0, employee_id: 0 });
    onServerFilterChange?.({});
  };

  const hasServerFilters = !!(serverFilters.head_quarter_id || serverFilters.location_id);
  const hasClientFilters = !!(searchTerm || clientFilters.type || clientFilters.zone_id || clientFilters.location_id || clientFilters.employee_id);
  const hasActiveFilters = hasServerFilters || hasClientFilters;

  const zoneOptions: SelectOption[] = zones.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const headQuaterOptions: SelectOption[] = headQuarters.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const locationOptions: SelectOption[] = filteredLocations.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const employeeOptions: SelectOption[] = employees.map((item) => ({
    value: item.id,
    label: item.user.name,
  }));

  const typeOptions = [
    { value: "chemist", label: "Chemist" },
    { value: "stockist", label: "Stockist" },
  ];

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading firms...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Search and Filter Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex flex-col md:flex-row gap-3 flex-wrap">
          {/* Search Bar */}
          <div className="flex-1 relative min-w-[180px]">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search name, contact, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* HQ Filter — server-side */}
          <select
            value={serverFilters.head_quarter_id ?? ''}
            onChange={(e) => handleHqChange(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="">All Headquarters</option>
            {headQuarters.map(hq => (
              <option key={hq.id} value={hq.id}>{hq.name}</option>
            ))}
          </select>

          {/* Location Filter — server-side, scoped to selected HQ */}
          <select
            value={serverFilters.location_id ?? ''}
            onChange={(e) => handleLocationServerChange(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="">All Locations</option>
            {hqFilteredLocations.map(loc => (
              <option key={loc.id} value={loc.id}>{loc.name}</option>
            ))}
          </select>

          {/* More Filters Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 border rounded-lg transition-colors whitespace-nowrap ${
              showFilters || hasClientFilters
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4 mr-2" />
            More
            {hasClientFilters && <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">●</span>}
          </button>

          {hasActiveFilters && (
            <button onClick={clearAllFilters} className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors">
              <X className="w-4 h-4 mr-1" />
              Clear
            </button>
          )}
        </div>

        {/* Expandable client-side filters */}
        {showFilters && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
              <Select
                options={typeOptions}
                onChange={(opt) => setClientFilters(prev => ({ ...prev, type: opt ? opt.value : "" }))}
                value={typeOptions.find(o => o.value === clientFilters.type) || null}
                isClearable placeholder="All Types"
                className="text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Zone</label>
              <Select
                options={zoneOptions}
                onChange={(opt) => setClientFilters(prev => ({ ...prev, zone_id: opt ? opt.value : 0 }))}
                value={zoneOptions.find(o => o.value === clientFilters.zone_id) || null}
                isClearable placeholder="All Zones"
                className="text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
              <Select
                options={locationOptions}
                onChange={(opt) => setClientFilters(prev => ({ ...prev, location_id: opt ? opt.value : 0 }))}
                value={locationOptions.find(o => o.value === clientFilters.location_id) || null}
                isClearable isDisabled={!serverFilters.head_quarter_id}
                placeholder={serverFilters.head_quarter_id ? "All Locations" : "Select HQ first"}
                className="text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Employee</label>
              <Select
                options={employeeOptions}
                onChange={(opt) => setClientFilters(prev => ({ ...prev, employee_id: opt ? opt.value : 0 }))}
                value={employeeOptions.find(o => o.value === clientFilters.employee_id) || null}
                isClearable placeholder="All Employees"
                className="text-sm"
              />
            </div>
          </div>
        )}

        {/* Results Summary */}
        <div className="mt-3 text-sm text-gray-600">
          {pagination ? (
            <>
              Showing {((pagination.current_page - 1) * pagination.per_page) + 1}–{Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
              <span className="font-medium">{pagination.total}</span> firms
              {hasActiveFilters && <span className="ml-1 text-blue-600">(filtered)</span>}
            </>
          ) : (
            <>Showing {filteredFirms.length} firms{hasActiveFilters && ' (filtered)'}</>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact Person
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact No.
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Zone
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Head Quarter
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Employee
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Address
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Latitude
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Longitude
              </th>
              {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created Date
              </th> */}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredFirms.length === 0 ? (
              <tr>
                <td colSpan={12} className="px-6 py-8 text-center text-gray-500">
                  {hasActiveFilters ? 'No firms match the current filters.' : 'No firms found.'}
                </td>
              </tr>
            ) : (
              filteredFirms.map((firm) => (
                <tr key={firm.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => onEdit(firm)}
                        className="text-blue-600 hover:text-blue-900 transition-colors"
                        title="Edit firm"
                      >
                        <Edit className="w-4 h-4" />
                      </button>

                      <button
                        onClick={async () => {
                          if (await toast.confirm('This action cannot be undone.', 'Delete firm?', 'Delete')) {
                            onDelete(firm.id!.toString());
                          }
                        }}
                        className="text-red-600 hover:text-red-900 transition-colors"
                        title="Delete firm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {firm?.name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {capitalizeFirstLetter(firm?.type)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {firm?.contact_person || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {firm?.contact_no || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {firm?.email || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {firm?.zone || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {firm?.head_quarter || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {firm?.location || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {firm.employee || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                    {firm.address || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                    {firm.latitude || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                    {firm.longitude || '-'}
                  </td>
                  {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {firm.created_date ? fmtDate(firm.created_date) : '-'}
                  </td> */}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer: count + pagination */}
      <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          {pagination
            ? <>Showing {((pagination.current_page - 1) * pagination.per_page) + 1}–{Math.min(pagination.current_page * pagination.per_page, pagination.total)} of <span className="font-medium">{pagination.total}</span></>
            : <>Total: <span className="font-medium">{firms.length}</span></>}
          {hasActiveFilters && <span className="ml-2 text-blue-600">(filtered)</span>}
        </p>

        {pagination && pagination.last_page > 1 && onPageChange && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => onPageChange(pagination.current_page - 1)}
              disabled={pagination.current_page === 1}
              className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {Array.from({ length: pagination.last_page }, (_, i) => i + 1)
              .filter(p => p === 1 || p === pagination.last_page || Math.abs(p - pagination.current_page) <= 1)
              .map((p, idx, arr) => (
                <React.Fragment key={p}>
                  {idx > 0 && arr[idx - 1] !== p - 1 && (
                    <span className="px-1 text-gray-400 text-sm">…</span>
                  )}
                  <button
                    onClick={() => onPageChange(p)}
                    className={`min-w-[32px] px-2 py-1.5 text-sm rounded-lg ${
                      p === pagination.current_page
                        ? 'bg-blue-600 text-white font-medium'
                        : 'border border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    {p}
                  </button>
                </React.Fragment>
              ))}

            <button
              onClick={() => onPageChange(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.last_page}
              className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FirmList;